import { useMutation } from "@tanstack/react-query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";

interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  relationship: "spouse" | "parent" | "sibling" | "friend" | "colleague" | "other";
  isPrimary: boolean;
  createdAt: string;
}

interface EmergencyContactData {
  contacts: EmergencyContact[];
  lastUpdated: string;
}

interface EmergencyContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  initialData: EmergencyContactData | null;
}

const RELATIONSHIP_OPTIONS = [
  { value: "spouse", label: "Cônjuge" },
  { value: "parent", label: "Pai/Mãe" },
  { value: "sibling", label: "Irmão/Irmã" },
  { value: "friend", label: "Amigo(a)" },
  { value: "colleague", label: "Colega" },
  { value: "other", label: "Outro" },
] as const;

function nowHHmm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function EmergencyContactDialog({
  open,
  onOpenChange,
  employeeId,
  initialData,
}: EmergencyContactDialogProps) {
  const { toast } = useToast();
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData?.contacts) {
      setContacts(initialData.contacts);
    } else {
      setContacts([]);
    }
  }, [initialData]);

  const updateMutation = useMutation({
    ...orpc.employees.updateField.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      toast({ description: `Contatos de emergência salvos às ${nowHHmm()}.` });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        description: error.message || "Falha ao salvar contatos de emergência. Tente novamente.",
      });
    },
  });

  const addContact = () => {
    if (contacts.length >= 3) {
      toast({
        variant: "destructive",
        description: "Limite atingido. Apenas 3 contatos de emergência são permitidos.",
      });
      return;
    }

    const newContact: EmergencyContact = {
      id: crypto.randomUUID(),
      name: "",
      phone: "",
      relationship: "other",
      isPrimary: contacts.length === 0,
      createdAt: new Date().toISOString(),
    };
    setContacts([...contacts, newContact]);
  };

  const removeContact = (id: string) => {
    const updated = contacts.filter((c) => c.id !== id);
    if (updated.length > 0 && !updated.some((c) => c.isPrimary)) {
      updated[0].isPrimary = true;
    }
    setContacts(updated);
  };

  const updateContact = (id: string, field: keyof EmergencyContact, value: any) => {
    setContacts(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${id}-${field}`];
      return next;
    });
  };

  const setPrimary = (id: string) => {
    setContacts(
      contacts.map((c) => ({
        ...c,
        isPrimary: c.id === id,
      })),
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    let hasError = false;

    if (contacts.length === 0) {
      toast({
        variant: "destructive",
        description: "Adicione pelo menos um contato de emergência.",
      });
      return false;
    }

    contacts.forEach((contact) => {
      if (!contact.name.trim()) {
        newErrors[`${contact.id}-name`] = "Nome obrigatório. Informe o contato.";
        hasError = true;
      }

      if (!contact.phone.trim()) {
        newErrors[`${contact.id}-phone`] = "Telefone obrigatório. Use o formato +55 11 99999-9999.";
        hasError = true;
      } else if (!isValidPhoneNumber(contact.phone, "BR")) {
        newErrors[`${contact.id}-phone`] = "Telefone inválido. Use o formato +55 11 99999-9999.";
        hasError = true;
      }
    });

    if (!contacts.some((c) => c.isPrimary)) {
      toast({
        variant: "destructive",
        description: "Marque pelo menos um contato como principal.",
      });
      return false;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleSave = () => {
    if (!validate()) return;

    const formattedContacts = contacts.map((c) => ({
      ...c,
      phone: parsePhoneNumber(c.phone, "BR")?.formatInternational() || c.phone,
    }));

    const data: EmergencyContactData = {
      contacts: formattedContacts,
      lastUpdated: new Date().toISOString(),
    };

    updateMutation.mutate({
      id: employeeId,
      field: "emergencyContact",
      value: JSON.stringify(data),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-modal max-w-2xl max-h-[80vh] overflow-y-auto"
        overlayClassName="glass-scrim"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>Contatos de emergência</DialogTitle>
            <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
          </div>
          <DialogDescription>
            Até 3 contatos. Pelo menos um precisa ser marcado como principal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {contacts.map((contact, index) => (
            <div
              key={contact.id}
              className="border-b border-[hsl(var(--card-line))] pb-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="body-strong text-[hsl(var(--ink-primary))]">
                  Contato {index + 1}
                  {contact.isPrimary && (
                    <span className="body-strong text-[hsl(var(--comp-coral))] ml-2">
                      Principal
                    </span>
                  )}
                </span>
                <div className="flex gap-3">
                  {!contact.isPrimary && (
                    <button
                      type="button"
                      className="text-sm underline text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))]"
                      onClick={() => setPrimary(contact.id)}
                    >
                      tornar principal
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-sm body-strong text-[hsl(var(--alert-red))] hover:text-[hsl(var(--comp-coral-deep))]"
                    onClick={() => removeContact(contact.id)}
                  >
                    Remover
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor={`name-${contact.id}`} className="caption-mono">
                    Nome
                  </Label>
                  <Input
                    id={`name-${contact.id}`}
                    value={contact.name}
                    onChange={(e) => updateContact(contact.id, "name", e.target.value)}
                    placeholder="Nome completo"
                  />
                  {errors[`${contact.id}-name`] && (
                    <p className="text-xs text-[hsl(var(--alert-red))]">
                      {errors[`${contact.id}-name`]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`phone-${contact.id}`} className="caption-mono">
                    Telefone
                  </Label>
                  <Input
                    id={`phone-${contact.id}`}
                    value={contact.phone}
                    onChange={(e) => updateContact(contact.id, "phone", e.target.value)}
                    placeholder="+55 11 99999-9999"
                    className="num-mono"
                  />
                  {errors[`${contact.id}-phone`] && (
                    <p className="text-xs text-[hsl(var(--alert-red))]">
                      {errors[`${contact.id}-phone`]}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor={`relationship-${contact.id}`} className="caption-mono">
                    Relação <span className="text-[hsl(var(--ink-muted))]">(opcional)</span>
                  </Label>
                  <SearchableSelect
                    id={`relationship-${contact.id}`}
                    value={contact.relationship}
                    onValueChange={(value: any) => updateContact(contact.id, "relationship", value)}
                    options={RELATIONSHIP_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    data-testid={`select-relationship-${contact.id}`}
                  />
                </div>
              </div>
            </div>
          ))}

          {contacts.length === 0 && (
            <p className="text-sm text-[hsl(var(--ink-muted))] py-6 text-center">
              Nenhum contato de emergência cadastrado ainda.
            </p>
          )}

          {contacts.length < 3 && (
            <Button variant="outline" className="w-full" onClick={addContact}>
              Adicionar contato
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending || contacts.length === 0}>
            {updateMutation.isPending ? "Salvando…" : "Salvar contatos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
